// v4 (spec-v4-structure-and-stage.md sections 3.2, 6.1, 6.5; ticket V4-C4): the web's client for characters and their
// sheets (V4-C1's CRUD, V4-C2's sheet routes, V4-C3's older looks) and the pure helpers the Character screen, the Cast
// cards and the older-look markers share: state words, version lines, prices, the "Update these" quote lines and the
// side-by-side compare.
//
// The web doesn't import core, so the sheet's parts and panels are copied here; keep them in step with
// product/core/src/sheets.ts (SHEET_PARTS, ANGLE_PANELS, EXPRESSION_PANELS, POSE_PANELS, PANEL_GROUPS).
//
// The source is swappable (installCharacters): the mock (src/mock/characters.ts) replaces it, like collections.ts.
import { call, normJob } from "./api";
import type { CastMember } from "./collections";
import type { JobView } from "./types";

// ------------------------------------------------------------------ the sheet (core sheets.ts, copied)

export const SHEET_PARTS = ["turnaround", "expressions", "poses"] as const;
export type SheetPart = (typeof SHEET_PARTS)[number];
export type PanelGroup = "angles" | "faces" | "poses";

/** The viewer's tabs: each is one part of the sheet and its panels, left to right (core PANEL_GROUPS). */
export const PANEL_GROUPS: { id: PanelGroup; label: string; part: SheetPart; word: string; panels: { id: string; label: string }[] }[] = [
  { id: "angles", label: "Angles", part: "turnaround", word: "angles", panels: [{ id: "front", label: "Front" }, { id: "three_quarter", label: "3/4" }, { id: "side", label: "Side" }, { id: "back", label: "Back" }] },
  {
    id: "faces",
    label: "Faces",
    part: "expressions",
    word: "faces",
    panels: [{ id: "neutral", label: "Neutral" }, { id: "happy", label: "Happy" }, { id: "sad", label: "Sad" }, { id: "angry", label: "Angry" }, { id: "surprised", label: "Surprised" }, { id: "thinking", label: "Thinking" }],
  },
  { id: "poses", label: "Poses", part: "poses", word: "poses", panels: [{ id: "standing", label: "Standing" }, { id: "walking", label: "Walking" }, { id: "sitting", label: "Sitting" }, { id: "pointing", label: "Pointing" }] },
];
export const groupOf = (id: PanelGroup) => PANEL_GROUPS.find((g) => g.id === id)!;
export const groupOfPart = (part: SheetPart) => PANEL_GROUPS.find((g) => g.part === part)!;

/** Today's prices at the default image model (spec 6.4, decision Q4): shown until the quote arrives. */
export const SHEET_CREDITS = 45;
export const PART_CREDITS = 15;

// ------------------------------------------------------------------ shapes (the api's views, normalised)

export type VersionStatus = "making" | "draft" | "approved" | "superseded" | "failed" | "discarded";
export type CharacterState = "none" | "making" | "draft" | "approved";
/** A picture: its id, its signed url and (V4-DL) its download link. */
export type Media = { assetId: string | null; url: string | null; downloadUrl?: string | null };
export type Colour = { name: string; hex: string };

/** A sheet version (V4-C1 VersionView). */
export type SheetVersion = {
  n: number;
  status: VersionStatus;
  look: string;
  palette: Colour[];
  /** the three part pictures by part */
  sheets: Partial<Record<SheetPart, Media>>;
  /** the 14 panels by panel id */
  panels: Record<string, Media>;
  basedOn: number | null;
  credits: number;
  note: string | null;
  legacy: boolean;
  createdAt: string | null;
  approvedAt: string | null;
  /** 2026-11-03: outfits and accessories (a picture each); one goes in when a scene names it */
  extras: { label: string; assetId: string; url: string | null }[];
};

export type CharacterSource = { kind: "description" | "drawing" | "photo"; assetId: string | null; url: string | null; consent: boolean; subject: "person" | "pet" | "object" | null };

/** A character (V4-C1 CharacterView). */
export type CharacterInfo = {
  id: string;
  slug: string;
  name: string;
  look: string;
  voice: string;
  voiceId: string | null;
  voiceName: string | null;
  collectionId: string | null;
  projectId: string | null;
  source: CharacterSource;
  approved: SheetVersion | null;
  latest: SheetVersion | null;
  state: CharacterState;
  lookChanged: boolean;
  olderShots: number;
};

/** A shot made with an older approved version (V4-C3). `hasClip` / `hasSounds` say what the update remakes. */
export type OlderShot = { projectId: string; title: string; sceneId: string; sceneIndex: number; usedN: number; hasClip: boolean; hasSounds: boolean };

export type CharacterDetail = { character: CharacterInfo; versions: SheetVersion[]; olderShots: OlderShot[] };

/** GET /characters/:id/sheet/quote (V4-C2). */
export type SheetQuote = { parts: SheetPart[]; credits: number; model: { id: string; short: string }; said: string | null; basedOn: number | null };

export type QuoteLine = { item: string; qty: number; unit: string; credits: number };
/** GET /characters/:id/older-shots/quote (V4-C3). */
export type OlderQuote = { shots: OlderShot[]; credits: number; breakdown: QuoteLine[]; spoken: string };

/** POST /characters/:id/sheet (V4-C2). */
export type SheetStarted = { character: CharacterInfo | null; version: SheetVersion | null; jobs: JobView[]; credits: number; balance: number | null; said: string | null };

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const s = (v: unknown) => (typeof v === "string" ? v : "");
const sOrNull = (v: unknown) => (typeof v === "string" && v ? v : null);
const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() && Number.isFinite(Number(v)) ? Number(v) : 0);
const nOrNull = (v: unknown) => (v == null ? null : n(v) || null);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const isoOf = (v: unknown) => (typeof v === "string" && v ? v : v instanceof Date ? v.toISOString() : null);

const VERSION_STATUSES: VersionStatus[] = ["making", "draft", "approved", "superseded", "failed", "discarded"];
const STATES: CharacterState[] = ["none", "making", "draft", "approved"];

const media = (v: unknown): Media => (isObj(v) ? { assetId: sOrNull(v.assetId ?? v.asset_id), url: sOrNull(v.url), downloadUrl: sOrNull(v.downloadUrl) } : typeof v === "string" ? { assetId: null, url: v, downloadUrl: null } : { assetId: null, url: null, downloadUrl: null });
const mediaMap = (v: unknown): Record<string, Media> => (isObj(v) ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, media(x)])) : {});

export function normVersion(v: unknown): SheetVersion {
  const o = isObj(v) ? v : {};
  const status = VERSION_STATUSES.includes(o.status as VersionStatus) ? (o.status as VersionStatus) : "draft";
  const sheets = mediaMap(o.sheets);
  return {
    n: n(o.n),
    status,
    look: s(o.look),
    palette: arr(o.palette)
      .filter(isObj)
      .map((c) => ({ name: s(c.name), hex: s(c.hex) }))
      .filter((c) => /^#[0-9a-f]{3,8}$/i.test(c.hex)),
    sheets: Object.fromEntries(SHEET_PARTS.filter((p) => sheets[p]?.url || sheets[p]?.assetId).map((p) => [p, sheets[p]!])),
    panels: mediaMap(o.panels),
    basedOn: nOrNull(o.basedOn ?? o.based_on),
    credits: n(o.credits),
    note: sOrNull(o.note),
    legacy: o.legacy === true,
    createdAt: isoOf(o.createdAt ?? o.created_at),
    approvedAt: isoOf(o.approvedAt ?? o.approved_at),
    extras: arr(o.extras)
      .filter(isObj)
      .map((x) => ({ label: s(x.label), assetId: s(x.assetId), url: sOrNull(x.url) }))
      .filter((x) => x.label && x.assetId),
  };
}

function normSource(v: unknown, url: unknown): CharacterSource {
  const o = isObj(v) ? v : {};
  const kind = o.kind === "drawing" || o.kind === "photo" ? o.kind : "description";
  const subject = o.subject === "person" || o.subject === "pet" || o.subject === "object" ? o.subject : kind === "photo" ? "person" : null;
  return { kind, assetId: sOrNull(o.assetId), url: sOrNull(o.url ?? url), consent: o.consent === true, subject };
}

export function normCharacterInfo(v: unknown): CharacterInfo {
  const o = isObj(v) && isObj(v.character) ? v.character : isObj(v) ? v : {};
  const approved = isObj(o.approved) ? normVersion(o.approved) : null;
  const latest = isObj(o.latest) ? normVersion(o.latest) : null;
  const state = STATES.includes(o.state as CharacterState) ? (o.state as CharacterState) : approved ? "approved" : "none";
  return {
    id: s(o.id),
    slug: s(o.slug) || s(o.id),
    name: s(o.name) || "Someone",
    look: s(o.look),
    voice: s(o.voice),
    voiceId: sOrNull(o.voiceId),
    voiceName: sOrNull(o.voiceName),
    collectionId: sOrNull(o.collectionId),
    projectId: sOrNull(o.projectId),
    source: normSource(o.source, o.sourceUrl),
    approved,
    latest,
    state,
    lookChanged: o.lookChanged === true,
    olderShots: Array.isArray(o.olderShots) ? o.olderShots.length : n(o.olderShots),
  };
}

export function normOlderShot(v: unknown): OlderShot {
  const o = isObj(v) ? v : {};
  return {
    projectId: s(o.projectId),
    title: s(o.title) || "A video",
    sceneId: s(o.sceneId),
    sceneIndex: n(o.sceneIndex),
    usedN: n(o.usedN) || 1,
    hasClip: o.hasClip === true || o.clip === true,
    hasSounds: o.hasSounds === true || o.sounds === true || o.sfx === true,
  };
}

/** GET /characters/:id: `{character, versions, olderShots}`; versions newest first, older shots by video then scene. */
export function normCharacterDetail(v: unknown): CharacterDetail {
  const o = isObj(v) ? v : {};
  const character = normCharacterInfo(o.character ?? o);
  const seen = new Set<number>();
  const versions = arr(o.versions)
    .map(normVersion)
    .filter((x) => x.n > 0 && !seen.has(x.n) && !!seen.add(x.n))
    .sort((a, b) => b.n - a.n);
  const older = arr(isObj(o.character) && Array.isArray(o.character.olderShots) ? o.character.olderShots : o.olderShots).map(normOlderShot);
  if (older.length && character.olderShots < older.length) character.olderShots = older.length;
  return { character, versions, olderShots: older };
}

export function normSheetQuote(v: unknown): SheetQuote {
  const o = isObj(v) ? v : {};
  const m = isObj(o.model) ? o.model : {};
  const parts = arr(o.parts).filter((p): p is SheetPart => SHEET_PARTS.includes(p as SheetPart));
  return { parts: parts.length ? parts : [...SHEET_PARTS], credits: n(o.credits), model: { id: s(m.id), short: s(m.short) }, said: sOrNull(o.said), basedOn: nOrNull(o.basedOn) };
}

export function normOlderQuote(v: unknown): OlderQuote {
  const o = isObj(v) ? v : {};
  const q = isObj(o.quote) ? o.quote : o;
  return {
    shots: arr(o.shots).map(normOlderShot),
    credits: n(q.credits),
    breakdown: arr(q.breakdown)
      .filter(isObj)
      .map((l) => ({ item: s(l.item), qty: n(l.qty) || 1, unit: s(l.unit), credits: n(l.credits) }))
      .filter((l) => l.item),
    spoken: s(q.spoken),
  };
}

// ------------------------------------------------------------------ words

/** A version's state, in plain words. */
export const VERSION_WORD: Record<VersionStatus, string> = {
  making: "Being drawn",
  draft: "Waiting for your OK",
  approved: "Approved",
  superseded: "Older",
  failed: "Didn't work",
  discarded: "Discarded",
};
/** The short word on a chip or in a picker ("v2 · to check"). */
export const VERSION_SHORT: Record<VersionStatus, string> = { making: "being drawn", draft: "to check", approved: "approved", superseded: "older", failed: "didn't work", discarded: "discarded" };
/** The tone a version's chip takes. */
export const VERSION_TONE: Record<VersionStatus, "busy" | "draft" | "done" | "off" | "bad"> = { making: "busy", draft: "draft", approved: "done", superseded: "off", failed: "bad", discarded: "off" };

/** A character's chip on cards: what it needs from you. */
export const STATE_WORD: Record<CharacterState, string> = { approved: "Approved", draft: "To check", making: "Making", none: "No sheet" };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "today", "yesterday", "Oct 28" (the versions list). */
export function dayWord(iso: string | null, now = new Date()): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = (x: Date) => Math.floor(Date.UTC(x.getFullYear(), x.getMonth(), x.getDate()) / 86_400_000);
  const diff = day(now) - day(d);
  if (diff === 0) return "today";
  if (diff === 1) return "yesterday";
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "v2 · Approved · today" (the versions list and the header). */
export function versionLine(v: Pick<SheetVersion, "n" | "status" | "createdAt" | "approvedAt">, now = new Date()): string {
  const when = dayWord(v.status === "approved" ? (v.approvedAt ?? v.createdAt) : v.createdAt, now);
  return [`v${v.n}`, VERSION_WORD[v.status], when].filter(Boolean).join(" · ");
}

/** The version the screen opens on: one still being made or waiting for an OK, else the approved one, else the newest. */
export function defaultVersion(versions: SheetVersion[], approvedN: number | null): SheetVersion | null {
  const live = versions.filter((v) => v.status !== "discarded");
  return live.find((v) => v.status === "making" || v.status === "draft") ?? live.find((v) => v.n === approvedN) ?? live[0] ?? versions[0] ?? null;
}

/** A version's best small picture: the front panel, else the turnaround. */
export const versionThumb = (v: SheetVersion | null | undefined) => v?.panels.front?.url ?? v?.sheets.turnaround?.url ?? null;

/** Whether the sheet can be redone a part at a time (V4-C2 needs an earlier version with every part). */
export const canRedoPart = (versions: SheetVersion[]) => versions.some((v) => v.status !== "discarded" && v.status !== "failed" && SHEET_PARTS.every((p) => v.sheets[p]));

/** "Redo the sheet · 45", "Make the sheet · 45", "Redo just the faces · 15". */
export function sheetButton(what: "all" | SheetPart, credits: number | null, hasSheet: boolean): string {
  const label = what === "all" ? (hasSheet ? "Redo the sheet" : "Make the sheet") : `Redo just the ${groupOfPart(what).word}`;
  return credits ? `${label} · ${credits}` : label;
}

/** Credits for one part from a whole-sheet quote (every part costs the same: V4-C2 `3 × stepCredits`). */
export const partCredits = (all: SheetQuote | null) => (all && all.parts.length ? Math.round(all.credits / all.parts.length) : PART_CREDITS);

// ------------------------------------------------------------------ older looks: the "Update these" quote

/** "scene 2", "scenes 2, 4 and 6". */
export function scenesText(idx: number[]): string {
  const xs = [...new Set(idx.filter((i) => i > 0))].sort((a, b) => a - b);
  if (!xs.length) return "";
  if (xs.length === 1) return `scene ${xs[0]}`;
  return `scenes ${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`;
}

export type OlderGroup = { projectId: string; title: string; scenes: number[]; sceneIds: string[]; what: string; count: number };

/** Older shots by video, in first-seen order: `Ep 1 "The Dark" · scenes 2 and 4 · frame + clip + sounds`. */
export function olderGroups(shots: OlderShot[]): OlderGroup[] {
  const by = new Map<string, OlderShot[]>();
  for (const x of shots) by.set(x.projectId, [...(by.get(x.projectId) ?? []), x]);
  return [...by.entries()].map(([projectId, xs]) => {
    const clip = xs.some((x) => x.hasClip);
    const sounds = xs.some((x) => x.hasSounds);
    return {
      projectId,
      title: xs[0]!.title,
      scenes: xs.map((x) => x.sceneIndex).sort((a, b) => a - b),
      sceneIds: xs.map((x) => x.sceneId),
      what: ["frame", clip && "clip", sounds && "sounds"].filter(Boolean).join(" + ") + (clip || sounds ? "" : " only"),
      count: xs.length,
    };
  });
}

/** "5 shots in 2 videos use v1" (the notice on the Character screen). */
export function olderNotice(shots: OlderShot[], count = shots.length): string {
  const videos = new Set(shots.map((x) => x.projectId)).size;
  const used = [...new Set(shots.map((x) => x.usedN))].sort((a, b) => a - b);
  const verb = count === 1 ? "uses" : "use";
  const what = used.length === 1 ? `v${used[0]}` : "an older look";
  const where = videos > 1 ? ` in ${videos} videos` : videos === 1 && shots[0] ? ` in ${shots[0].title}` : "";
  return `${count} shot${count === 1 ? "" : "s"}${where} ${verb} ${what}`;
}

export type PriceLine = { label: string; credits: number };

/** A quote's breakdown as the sheet's lines: "5 frames · 75", "2 clips, Kling 2.5, 5 s · 170". */
export function quoteLines(q: Pick<OlderQuote, "breakdown">): PriceLine[] {
  return q.breakdown.map((l) => ({ label: /^\d/.test(l.item) ? l.item : `${l.qty} ${l.item}`, credits: Math.round(l.credits) }));
}

/** The total line, with the balance when known: "301 credits · you have 740". */
export function totalLine(credits: number, balance: number | null | undefined): string {
  return `${credits} credit${credits === 1 ? "" : "s"}${balance != null ? ` · you have ${balance}` : ""}`;
}

// ------------------------------------------------------------------ compare two versions

export type ComparePair = { left: SheetVersion; right: SheetVersion };

/** The two versions a compare opens on: the approved one beside the newest draft, else the two newest. */
export function comparePair(versions: SheetVersion[], approvedN: number | null): ComparePair | null {
  const live = versions.filter((v) => v.status !== "discarded" && v.status !== "failed").sort((a, b) => b.n - a.n);
  if (live.length < 2) return null;
  const approved = live.find((v) => v.n === approvedN);
  const newest = live[0]!;
  if (approved && approved.n !== newest.n) return { left: approved, right: newest };
  return { left: live[1]!, right: live[0]! };
}

/** The panels of one tab, side by side: `[{id, label, left, right}]`. */
export function compareRows(pair: ComparePair, group: PanelGroup) {
  return groupOf(group).panels.map((p) => ({ id: p.id, label: p.label, left: pair.left.panels[p.id]?.url ?? null, right: pair.right.panels[p.id]?.url ?? null }));
}

/** Parts the newer version drew again (a part copied from the older one keeps its picture). */
export function changedParts(a: SheetVersion, b: SheetVersion): SheetPart[] {
  return SHEET_PARTS.filter((p) => {
    const x = a.sheets[p];
    const y = b.sheets[p];
    if (!x && !y) return false;
    const key = (m: Media | undefined) => m?.assetId ?? m?.url ?? null;
    return key(x) !== key(y);
  });
}

/** Colours that changed between two versions, by name: `[{name, from, to}]` (from/to null when added or dropped). */
export function paletteDiff(a: Colour[], b: Colour[]): { name: string; from: string | null; to: string | null }[] {
  const lower = (cs: Colour[]) => new Map(cs.map((c) => [c.name.toLowerCase(), c]));
  const A = lower(a);
  const B = lower(b);
  const names = [...new Set([...A.keys(), ...B.keys()])];
  return names
    .map((k) => ({ name: (B.get(k) ?? A.get(k))!.name, from: A.get(k)?.hex.toUpperCase() ?? null, to: B.get(k)?.hex.toUpperCase() ?? null }))
    .filter((d) => d.from !== d.to);
}

/** One line on what's different: "v2 redrew the faces and poses. Scarf #2B5DAB → #3A6EA5. New description." */
export function compareSummary(pair: ComparePair): string {
  const { left, right } = pair;
  const parts = changedParts(left, right).map((p) => groupOfPart(p).word);
  const out: string[] = [];
  if (parts.length === SHEET_PARTS.length) out.push(`v${right.n} is a whole new sheet.`);
  else if (parts.length) out.push(`v${right.n} redrew the ${parts.join(" and ")}.`);
  else out.push(`v${right.n} uses the same pictures as v${left.n}.`);
  const cols = paletteDiff(left.palette, right.palette).slice(0, 3);
  if (cols.length) out.push(cols.map((c) => (c.from && c.to ? `${cap(c.name)} ${c.from} → ${c.to}` : c.to ? `${cap(c.name)} ${c.to} is new` : `${cap(c.name)} is gone`)).join(". ") + ".");
  if (left.look.trim() !== right.look.trim() && right.look.trim()) out.push("New description.");
  if (right.note) out.push(`Asked for: “${right.note}”`);
  return out.join(" ");
}
const cap = (t: string) => (t ? t[0]!.toUpperCase() + t.slice(1) : t);

// ------------------------------------------------------------------ jobs

/** A sheet job's part, from its step ("Pip's faces is being drawn") when the api doesn't say. */
export function jobPart(j: Pick<JobView, "step">): SheetPart | null {
  const t = (j.step ?? "").toLowerCase();
  if (/turnaround|angle/.test(t)) return "turnaround";
  if (/face|expression/.test(t)) return "expressions";
  if (/pose/.test(t)) return "poses";
  return null;
}

/** The character's sheet jobs still running, oldest first (followed by characterId: they have no project). */
export function sheetJobs(jobs: Record<string, JobView>, characterId: string): JobView[] {
  return Object.values(jobs)
    .filter((j) => j.kind === "character" && j.characterId === characterId && (j.status === "queued" || j.status === "running"))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** One progress number for a sheet being made (each part counts the same). */
export const sheetProgress = (jobs: Pick<JobView, "progress" | "status">[]) => (jobs.length ? jobs.reduce((t, j) => t + (j.status === "done" ? 1 : j.progress), 0) / jobs.length : 0);

/** A character as a cast card shows it (collections.ts CastMember): the version to look at is the one waiting. */
export function castMember(c: CharacterInfo): CastMember {
  const shown = c.state === "draft" || c.state === "making" ? c.latest : (c.approved ?? c.latest);
  return {
    id: c.id,
    slug: c.slug,
    name: c.name,
    look: c.look,
    voiceName: c.voiceName,
    state: c.state,
    approvedN: c.approved?.n ?? null,
    latestN: c.latest?.n ?? null,
    thumbUrl: versionThumb(shown) ?? versionThumb(c.approved),
    olderShots: c.olderShots,
  };
}

/** The character screen's path: inside a collection, else inside a video. `key` is the id (or the script's slug). */
export function characterPath(at: { collectionId?: string | null; projectId?: string | null }, key: string): string {
  const enc = encodeURIComponent;
  if (at.collectionId) return `/k/${enc(at.collectionId)}/cast/${enc(key)}`;
  return `/p/${enc(at.projectId ?? "")}/cast/${enc(key)}`;
}

/** Older-look marks per scene for one video: sceneId -> who is drawn with an older sheet there. */
export function olderMarks(details: Pick<CharacterDetail, "character" | "olderShots">[], projectId: string): Map<string, { names: string[]; usedN: number; approvedN: number | null }> {
  const out = new Map<string, { names: string[]; usedN: number; approvedN: number | null }>();
  for (const d of details)
    for (const x of d.olderShots) {
      if (x.projectId !== projectId) continue;
      const m = out.get(x.sceneId) ?? { names: [], usedN: x.usedN, approvedN: d.character.approved?.n ?? null };
      if (!m.names.includes(d.character.name)) m.names.push(d.character.name);
      m.usedN = Math.min(m.usedN, x.usedN);
      out.set(x.sceneId, m);
    }
  return out;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (v: string) => UUID.test(v);

// ------------------------------------------------------------------ the source (http, or the mock)

export type CharacterEdit = { name?: string; look?: string; voice?: string; voiceId?: string | null };
export type SheetBody = { part?: "all" | SheetPart; note?: string | null; model?: string | null; from?: { assetId: string } | null };

export type CharactersSource = {
  /** GET /characters/:id */
  get(id: string): Promise<CharacterDetail>;
  /** GET /projects/:id/characters */
  ofProject(projectId: string): Promise<CharacterInfo[]>;
  /** GET /collections/:id/characters */
  ofCollection(collectionId: string): Promise<CharacterInfo[]>;
  /** PATCH /characters/:id */
  edit(id: string, b: CharacterEdit): Promise<CharacterInfo>;
  /** GET /characters/:id/sheet/quote */
  quote(id: string, part?: "all" | SheetPart): Promise<SheetQuote>;
  /** POST /characters/:id/sheet */
  makeSheet(id: string, b: SheetBody): Promise<SheetStarted>;
  approve(id: string, n: number): Promise<CharacterInfo>;
  discard(id: string, n: number): Promise<CharacterInfo>;
  /** GET /characters/:id/older-shots/quote (V4-C3) */
  olderQuote(id: string, projectIds?: string[]): Promise<OlderQuote>;
  /** POST /characters/:id/older-shots/update (V4-C3): refused with 409 PRICE_CHANGED when the price went up */
  updateOlder(id: string, b: { projectIds?: string[]; sceneIds?: string[]; quotedCredits: number }): Promise<{ jobs: JobView[] }>;
  /** 2026-11-03: keep a video's older shots as drawn (free; the flag goes) */
  keepOlder(projectId: string, b: { characterId?: string; sceneIds?: string[] }): Promise<{ kept: number }>;
  /** 2026-11-03: an outfit or accessory on the approved sheet (a library picture and its name), and taking one off */
  addExtra(id: string, b: { assetId: string; label: string }): Promise<void>;
  removeExtra(id: string, assetId: string): Promise<void>;
};

const enc = encodeURIComponent;
const listOf = (v: unknown, ...keys: string[]) => {
  if (Array.isArray(v)) return v;
  if (isObj(v)) for (const k of keys) if (Array.isArray(v[k])) return v[k] as unknown[];
  return [];
};
const jobsOf = (v: unknown): JobView[] => listOf(isObj(v) ? v.jobs : v).map(normJob);

export const httpCharacters: CharactersSource = {
  async get(id) {
    return normCharacterDetail(await call("GET", `characters/${enc(id)}`));
  },
  async ofProject(projectId) {
    return listOf(await call("GET", `projects/${enc(projectId)}/characters`), "characters").map(normCharacterInfo);
  },
  async ofCollection(collectionId) {
    return listOf(await call("GET", `collections/${enc(collectionId)}/characters`), "characters").map(normCharacterInfo);
  },
  async edit(id, b) {
    return normCharacterInfo(await call("PATCH", `characters/${enc(id)}`, b));
  },
  async quote(id, part = "all") {
    return normSheetQuote(await call("GET", `characters/${enc(id)}/sheet/quote?part=${enc(part)}`));
  },
  async makeSheet(id, b) {
    const r = await call("POST", `characters/${enc(id)}/sheet`, b);
    const o = isObj(r) ? r : {};
    return {
      character: isObj(o.character) ? normCharacterInfo(o.character) : null,
      version: isObj(o.version) ? normVersion(o.version) : null,
      jobs: jobsOf(o),
      credits: n(o.credits),
      balance: typeof o.balance === "number" ? o.balance : null,
      said: sOrNull(o.said),
    };
  },
  async approve(id, v) {
    return normCharacterInfo(await call("POST", `characters/${enc(id)}/versions/${v}/approve`, {}));
  },
  async discard(id, v) {
    return normCharacterInfo(await call("POST", `characters/${enc(id)}/versions/${v}/discard`, {}));
  },
  async olderQuote(id, projectIds) {
    return normOlderQuote(await call("GET", `characters/${enc(id)}/older-shots/quote${projectIds?.length ? `?projectIds=${enc(projectIds.join(","))}` : ""}`));
  },
  async updateOlder(id, b) {
    return { jobs: jobsOf(await call("POST", `characters/${enc(id)}/older-shots/update`, { ...b, confirmed: true })) };
  },
  async addExtra(id, b) {
    await call("POST", `characters/${enc(id)}/extras`, b);
  },
  async removeExtra(id, assetId) {
    await call("DELETE", `characters/${enc(id)}/extras/${enc(assetId)}`);
  },
  async keepOlder(projectId, b) {
    const r = (await call("POST", `projects/${enc(projectId)}/older-shots/keep`, b)) as { kept?: unknown } | null;
    return { kept: Number(r?.kept) || 0 };
  },
};

let source: CharactersSource = httpCharacters;
export function installCharacters(src: Partial<CharactersSource>) {
  source = { ...source, ...src };
}
/** Always delegates to the installed source (http, or the mock under VITE_MOCK=1). */
export const characters: CharactersSource = new Proxy({} as CharactersSource, {
  get: (_t, k: keyof CharactersSource) => {
    const v = source[k];
    return typeof v === "function" ? (v as (...a: unknown[]) => unknown).bind(source) : v;
  },
});

/** The character a route names: a uuid, or (inside a video) the script's slug, matched against the video's cast. */
export async function findCharacter(at: { projectId?: string | null; collectionId?: string | null }, key: string): Promise<CharacterDetail> {
  if (isUuid(key) || (!at.projectId && !at.collectionId)) return characters.get(key);
  const list = at.projectId ? await characters.ofProject(at.projectId) : await characters.ofCollection(at.collectionId!);
  const hit = list.find((c) => c.id === key) ?? list.find((c) => c.slug.toLowerCase() === key.toLowerCase());
  if (!hit) return characters.get(key);
  return characters.get(hit.id);
}
